import React, { useState, useEffect } from 'react';
import { Modal, Input, Button, Card } from '../Common';

const SEOSettingsModal = ({ isOpen, onClose, articleData, onSave }) => {
  const [activeTab, setActiveTab] = useState('basics');
  const [seoData, setSeoData] = useState({
    // Basic SEO
    metaTitle: '',
    metaDescription: '',
    urlSlug: '',
    focusKeyword: '',
    allowIndexing: true,
    
    // Advanced
    robotTags: 'index,follow',
    schemaType: 'Article',
    indexingStatus: 'pending',
    canonicalUrl: '',
    structuredDataMarkup: '',
    
    // Social Share
    ogTitle: '',
    ogDescription: '',
    ogImage: '',
    twitterTitle: '',
    twitterDescription: '',
    twitterImage: '',
  });
  
  const [seoScore, setSeoScore] = useState({ score: 0, issues: [] });
  const [isAnalyzing, setIsAnalyzing] = useState(false);

  useEffect(() => {
    if (articleData) {
      setSeoData({
        metaTitle: articleData.metaTitle || articleData.title || '',
        metaDescription: articleData.metaDescription || articleData.excerpt || '',
        urlSlug: articleData.urlSlug || generateSlug(articleData.title || ''),
        focusKeyword: articleData.focusKeyword || '',
        allowIndexing: articleData.allowIndexing !== false,
        robotTags: articleData.robotTags || 'index,follow',
        schemaType: articleData.schemaType || 'Article',
        indexingStatus: articleData.indexingStatus || 'pending',
        canonicalUrl: articleData.canonicalUrl || '',
        structuredDataMarkup: articleData.structuredDataMarkup || '',
        ogTitle: articleData.ogTitle || articleData.title || '',
        ogDescription: articleData.ogDescription || articleData.excerpt || '',
        ogImage: articleData.ogImage || articleData.featuredImage || '',
        twitterTitle: articleData.twitterTitle || articleData.title || '',
        twitterDescription: articleData.twitterDescription || articleData.excerpt || '',
        twitterImage: articleData.twitterImage || articleData.featuredImage || '',
      });
    }
  }, [articleData]);

  const generateSlug = (title) => {
    return title
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/(^-|-$)/g, '');
  };

  const handleChange = (field, value) => {
    setSeoData(prev => ({ ...prev, [field]: value }));
  };

  const analyzeSEO = () => {
    setIsAnalyzing(true);
    
    // Simulate SEO analysis
    setTimeout(() => {
      const issues = [];
      let score = 100;

      // Check meta title
      if (!seoData.metaTitle) {
        issues.push({ type: 'error', message: 'Meta title is missing', priority: 'CRITICAL' });
        score -= 20;
      } else if (seoData.metaTitle.length < 30) {
        issues.push({ type: 'warning', message: 'Meta title is too short (< 30 chars)', priority: 'HIGH' });
        score -= 10;
      } else if (seoData.metaTitle.length > 60) {
        issues.push({ type: 'warning', message: 'Meta title is too long (> 60 chars)', priority: 'MEDIUM' });
        score -= 5;
      }

      // Check meta description
      if (!seoData.metaDescription) {
        issues.push({ type: 'error', message: 'Meta description is missing', priority: 'HIGH' });
        score -= 15;
      } else if (seoData.metaDescription.length < 120) {
        issues.push({ type: 'warning', message: 'Meta description is too short', priority: 'MEDIUM' });
        score -= 5;
      } else if (seoData.metaDescription.length > 160) {
        issues.push({ type: 'warning', message: 'Meta description is too long', priority: 'LOW' });
        score -= 3;
      }

      // Check focus keyword
      if (!seoData.focusKeyword) {
        issues.push({ type: 'warning', message: 'Focus keyword is not set', priority: 'MEDIUM' });
        score -= 10;
      } else {
        // Check if keyword is in title
        if (!seoData.metaTitle.toLowerCase().includes(seoData.focusKeyword.toLowerCase())) {
          issues.push({ type: 'warning', message: 'Focus keyword not found in title', priority: 'MEDIUM' });
          score -= 5;
        }
        // Check if keyword is in description
        if (!seoData.metaDescription.toLowerCase().includes(seoData.focusKeyword.toLowerCase())) {
          issues.push({ type: 'info', message: 'Focus keyword not found in description', priority: 'LOW' });
          score -= 3;
        }
      }

      // Check URL slug
      if (!seoData.urlSlug) {
        issues.push({ type: 'error', message: 'URL slug is missing', priority: 'CRITICAL' });
        score -= 15;
      }

      // Check images
      if (!seoData.ogImage) {
        issues.push({ type: 'warning', message: 'Social share image is missing', priority: 'MEDIUM' });
        score -= 5;
      }

      // Indexing check
      if (seoData.allowIndexing) {
        issues.push({ type: 'success', message: 'Post will be indexed by search engines', priority: 'INFO' });
      }

      setSeoScore({ score: Math.max(0, score), issues });
      setIsAnalyzing(false);
    }, 1000);
  };

  const handleSave = () => {
    onSave(seoData);
    onClose();
  };

  const getScoreColor = (score) => {
    if (score >= 80) return 'text-green-600';
    if (score >= 60) return 'text-yellow-600';
    return 'text-red-600';
  };

  const getPriorityColor = (priority) => {
    switch (priority) {
      case 'CRITICAL': return 'bg-red-100 text-red-800';
      case 'HIGH': return 'bg-orange-100 text-orange-800';
      case 'MEDIUM': return 'bg-yellow-100 text-yellow-800';
      case 'LOW': return 'bg-blue-100 text-blue-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const tabs = [
    { id: 'assistant', label: 'Assistant' },
    { id: 'basics', label: 'Basics' },
    { id: 'advanced', label: 'Advanced' },
    { id: 'social', label: 'Social share' },
  ];

  if (!isOpen) return null;

  return (
    <Modal isOpen={isOpen} onClose={onClose} title="SEO Settings" size="lg">
      <div className="space-y-6">
        {/* Tabs */}
        <div className="flex border-b border-gray-200">
          {tabs.map(tab => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
                activeTab === tab.id
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700'
              }`}
            >
              {tab.label}
            </button>
          ))}
        </div>

        {/* Assistant Tab */}
        {activeTab === 'assistant' && (
          <div className="space-y-4">
            {/* Focus Keyword */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Focus keyword
              </label>
              <p className="text-xs text-gray-500 mb-2">
                You'll get more tasks based on this keyword phrase.
              </p>
              <div className="flex gap-2">
                <Input
                  value={seoData.focusKeyword}
                  onChange={(e) => handleChange('focusKeyword', e.target.value)}
                  placeholder="e.g., carrot cake recipe, top tech trends"
                  className="flex-1"
                />
                <Button variant="outline" size="sm" disabled={!seoData.focusKeyword?.trim()} onClick={() => {
                  if (!seoData.focusKeyword?.trim()) return;
                  setIsAnalyzing(true);
                  setTimeout(() => {
                    const keyword = seoData.focusKeyword.trim();
                    const suggestions = [
                      `${keyword} guide`,
                      `${keyword} tips`,
                      `${keyword} best practices`,
                      `what is ${keyword}`,
                      `${keyword} for beginners`,
                    ];
                    const issues = seoScore.issues || [];
                    suggestions.forEach(s => {
                      issues.push({ type: 'info', message: `Try: "${s}"`, priority: 'LOW' });
                    });
                    setSeoScore(prev => ({ ...prev, issues: issues.slice(0, 20) }));
                    setIsAnalyzing(false);
                  }, 800);
                }}>
                  Get Keyword Ideas
                </Button>
              </div>
            </div>

            {/* Analyze Button */}
            <Button onClick={analyzeSEO} disabled={isAnalyzing} className="w-full">
              {isAnalyzing ? 'Analyzing...' : 'Analyze SEO'}
            </Button>

            {/* SEO Score */}
            {seoScore.issues.length > 0 && (
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <span className="text-sm font-medium">SEO Score</span>
                  <span className={`text-2xl font-bold ${getScoreColor(seoScore.score)}`}>
                    {seoScore.score}/100
                  </span>
                </div>

                {/* Issues List */}
                <div className="space-y-2">
                  {seoScore.issues.map((issue, index) => (
                    <div
                      key={index}
                      className={`flex items-center justify-between p-3 rounded-lg ${
                        issue.type === 'error' ? 'bg-red-50' :
                        issue.type === 'warning' ? 'bg-yellow-50' :
                        issue.type === 'success' ? 'bg-green-50' : 'bg-gray-50'
                      }`}
                    >
                      <div className="flex items-center gap-2">
                        <span className={`w-2 h-2 rounded-full ${
                          issue.type === 'error' ? 'bg-red-500' :
                          issue.type === 'warning' ? 'bg-yellow-500' :
                          issue.type === 'success' ? 'bg-green-500' : 'bg-gray-500'
                        }`} />
                        <span className="text-sm">{issue.message}</span>
                      </div>
                      <span className={`text-xs px-2 py-1 rounded ${getPriorityColor(issue.priority)}`}>
                        {issue.priority}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Post Insights */}
            <Card className="p-4">
              <h4 className="font-medium text-gray-900 mb-3">Post Insights</h4>
              <p className="text-sm text-gray-600">
                Get the index status for this post. Publish your post, then come back here to inspect your site and get the index status on Google.
              </p>
            </Card>
          </div>
        )}

        {/* Basics Tab */}
        {activeTab === 'basics' && (
          <div className="space-y-4">
            {/* Google Preview */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Preview on Google
              </label>
              <button type="button" className="text-xs text-blue-600 hover:underline mb-2 block">
                When will changes show live?
              </button>
              <div className="border rounded-lg p-4 bg-white">
                <div className="flex items-center gap-2 mb-1">
                  <div className="w-6 h-6 rounded-full bg-blue-600 flex items-center justify-center">
                    <span className="text-white text-xs font-bold">S</span>
                  </div>
                  <span className="text-sm text-gray-600">studentsherald.com</span>
                </div>
                <h3 className="text-blue-800 text-lg hover:underline cursor-pointer">
                  {seoData.metaTitle || 'Add a title'}
                </h3>
                <p className="text-sm text-gray-600 line-clamp-2">
                  {seoData.metaDescription || 'Add a description'}
                </p>
              </div>
            </div>

            {/* Title Tag */}
            <div>
              <div className="flex items-center justify-between mb-1">
                <label className="text-sm font-medium text-gray-700">
                  Title tag
                </label>
                <span className="text-xs text-gray-500">
                  {seoData.metaTitle.length}/60
                </span>
              </div>
              <Input
                value={seoData.metaTitle}
                onChange={(e) => handleChange('metaTitle', e.target.value)}
                placeholder="Search engines may show a different title"
              />
            </div>

            {/* Meta Description */}
            <div>
              <div className="flex items-center justify-between mb-1">
                <label className="text-sm font-medium text-gray-700">
                  Meta description
                </label>
                <span className="text-xs text-gray-500">
                  {seoData.metaDescription.length}/160
                </span>
              </div>
              <textarea
                value={seoData.metaDescription}
                onChange={(e) => handleChange('metaDescription', e.target.value)}
                placeholder="Search engines may show a different description"
                rows={3}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>

            {/* URL Slug */}
            <div>
              <div className="flex items-center justify-between mb-1">
                <label className="text-sm font-medium text-gray-700">
                  URL slug
                </label>
                <span className="text-xs text-gray-500">
                  {seoData.urlSlug.length}
                </span>
              </div>
              <div className="flex items-center gap-2">
                <span className="text-gray-500 text-sm mr-1">/</span>
                <Input
                  value={seoData.urlSlug}
                  onChange={(e) => handleChange('urlSlug', e.target.value)}
                  placeholder="Add the last part of the URL"
                  className="flex-1"
                />
                <button
                  type="button"
                  onClick={() => {
                    const slug = generateSlug(articleData?.title || seoData.metaTitle || '');
                    handleChange('urlSlug', slug);
                  }}
                  className="px-3 py-2 text-sm text-blue-600 hover:text-blue-800 font-medium whitespace-nowrap"
                  title="Regenerate from title"
                >
                  Regenerate
                </button>
              </div>
              <button type="button" className="text-xs text-blue-600 hover:underline mt-1 block">
                Go to URL
              </button>
            </div>

            {/* Index Status */}
            <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
              <div>
                <span className="text-sm font-medium text-gray-700">Index status</span>
                <p className="text-xs text-gray-500">Let search engines index this page</p>
              </div>
              <label className="relative inline-flex items-center cursor-pointer">
                <input
                  type="checkbox"
                  checked={seoData.allowIndexing}
                  onChange={(e) => handleChange('allowIndexing', e.target.checked)}
                  className="sr-only peer"
                />
                <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
              </label>
            </div>
          </div>
        )}

        {/* Advanced Tab */}
        {activeTab === 'advanced' && (
          <div className="space-y-4">
            {/* Schema Type */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Schema Type
              </label>
              <select
                value={seoData.schemaType}
                onChange={(e) => handleChange('schemaType', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="Article">Article</option>
                <option value="NewsArticle">News Article</option>
                <option value="BlogPosting">Blog Posting</option>
                <option value="ScholarlyArticle">Scholarly Article</option>
              </select>
              <p className="text-xs text-gray-500 mt-1">
                Select the structured data type for this content
              </p>
            </div>

            {/* Robot Tags */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Robot Meta Tags
              </label>
              <div className="space-y-2">
                <label className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    checked={seoData.robotTags.includes('index')}
                    onChange={(e) => {
                      let tags = seoData.robotTags.split(',').map(t => t.trim());
                      if (e.target.checked) {
                        if (!tags.includes('index')) tags.push('index');
                        tags = tags.filter(t => t !== 'noindex');
                      } else {
                        tags = tags.filter(t => t !== 'index');
                        if (!tags.includes('noindex')) tags.push('noindex');
                      }
                      handleChange('robotTags', tags.join(','));
                    }}
                    className="rounded border-gray-300"
                  />
                  <span className="text-sm">Index - Allow search engines to index this page</span>
                </label>
                <label className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    checked={seoData.robotTags.includes('follow')}
                    onChange={(e) => {
                      let tags = seoData.robotTags.split(',').map(t => t.trim());
                      if (e.target.checked) {
                        if (!tags.includes('follow')) tags.push('follow');
                        tags = tags.filter(t => t !== 'nofollow');
                      } else {
                        tags = tags.filter(t => t !== 'follow');
                        if (!tags.includes('nofollow')) tags.push('nofollow');
                      }
                      handleChange('robotTags', tags.join(','));
                    }}
                    className="rounded border-gray-300"
                  />
                  <span className="text-sm">Follow - Allow search engines to follow links</span>
                </label>
                <label className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    checked={seoData.robotTags.includes('noarchive')}
                    onChange={(e) => {
                      const tags = seoData.robotTags.split(',').map(t => t.trim());
                      if (e.target.checked) {
                        if (!tags.includes('noarchive')) tags.push('noarchive');
                      } else {
                        const filtered = tags.filter(t => t !== 'noarchive');
                        handleChange('robotTags', filtered.join(','));
                      }
                      handleChange('robotTags', tags.join(','));
                    }}
                    className="rounded border-gray-300"
                  />
                  <span className="text-sm">No Archive - Prevent cached copy</span>
                </label>
              </div>
              <p className="text-xs text-gray-500 mt-2">
                Current: {seoData.robotTags}
              </p>
            </div>

            {/* Indexing Status */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Indexing Status
              </label>
              <select
                value={seoData.indexingStatus}
                onChange={(e) => handleChange('indexingStatus', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="pending">Pending</option>
                <option value="submitted">Submitted</option>
                <option value="indexed">Indexed</option>
                <option value="failed">Failed</option>
              </select>
              <p className="text-xs text-gray-500 mt-1">
                Track the indexing status with search engines
              </p>
            </div>

            {/* Structured Data Markup */}
            <Card className="p-4 cursor-pointer hover:bg-gray-50">
              <div className="flex items-center justify-between">
                <div>
                  <h4 className="font-medium text-gray-900">Structured data markup</h4>
                  <p className="text-sm text-gray-500">
                    Make your posts eligible for rich results and improve how LLMs like ChatGPT process your content.
                  </p>
                </div>
                <span className="text-gray-400">›</span>
              </div>
            </Card>

            {/* Canonical URL */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Canonical URL
              </label>
              <Input
                value={seoData.canonicalUrl}
                onChange={(e) => handleChange('canonicalUrl', e.target.value)}
                placeholder="https://studentsherald.com/article/..."
              />
              <p className="text-xs text-gray-500 mt-1">
                Use this to specify the preferred URL for this content if it appears on multiple URLs.
              </p>
            </div>
          </div>
        )}

        {/* Social Share Tab */}
        {activeTab === 'social' && (
          <div className="space-y-6">
            {/* Facebook / Open Graph */}
            <div>
              <h4 className="font-medium text-gray-900 mb-3">Facebook / Open Graph</h4>
              <div className="space-y-3">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    OG Title
                  </label>
                  <Input
                    value={seoData.ogTitle}
                    onChange={(e) => handleChange('ogTitle', e.target.value)}
                    placeholder="Title for Facebook sharing"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    OG Description
                  </label>
                  <textarea
                    value={seoData.ogDescription}
                    onChange={(e) => handleChange('ogDescription', e.target.value)}
                    placeholder="Description for Facebook sharing"
                    rows={2}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    OG Image URL
                  </label>
                  <Input
                    value={seoData.ogImage}
                    onChange={(e) => handleChange('ogImage', e.target.value)}
                    placeholder="https://..."
                  />
                </div>
              </div>
            </div>

            {/* Twitter */}
            <div>
              <h4 className="font-medium text-gray-900 mb-3">Twitter</h4>
              <div className="space-y-3">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Twitter Title
                  </label>
                  <Input
                    value={seoData.twitterTitle}
                    onChange={(e) => handleChange('twitterTitle', e.target.value)}
                    placeholder="Title for Twitter sharing"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Twitter Description
                  </label>
                  <textarea
                    value={seoData.twitterDescription}
                    onChange={(e) => handleChange('twitterDescription', e.target.value)}
                    placeholder="Description for Twitter sharing"
                    rows={2}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Twitter Image URL
                  </label>
                  <Input
                    value={seoData.twitterImage}
                    onChange={(e) => handleChange('twitterImage', e.target.value)}
                    placeholder="https://..."
                  />
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Action Buttons */}
        <div className="flex justify-end gap-3 pt-4 border-t">
          <Button variant="outline" onClick={onClose}>
            Cancel
          </Button>
          <Button onClick={handleSave}>
            Save SEO Settings
          </Button>
        </div>
      </div>
    </Modal>
  );
};

export default SEOSettingsModal;
